import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PanelLoading } from "../../components/Loading";
import { exchangeRatesApi, type ExchangeRateList } from "../../api/exchangeRates";
import type { ApiError } from "../../api/client";
import { formatDate, formatNumber } from "../../lib/format";

/** Today in Belgrade as yyyy-MM-dd: the list of a day is Bankom's day, wherever the browser is. */
function todayInBelgrade(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Belgrade" }).format(new Date());
}

function shiftDay(day: string, days: number): string {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function AdminExchangeRatesPage() {
  const { t } = useTranslation();
  const [day, setDay] = useState(todayInBelgrade);
  const [list, setList] = useState<ExchangeRateList | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    setList(null);
    setFailed(null);
    exchangeRatesApi
      .get(day)
      .then((result) => { if (current) setList(result); })
      .catch((err: ApiError) => {
        if (current) setFailed(err?.status === 503 ? t("exchangeRates.erpUnavailable") : (err?.message ?? t("exchangeRates.erpUnavailable")));
      });
    return () => { current = false; };
  }, [day, t]);

  const rate = (value: number) =>
    value ? formatNumber(value, undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 }) : "—";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <header style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <h1 style={{ margin: 0, fontSize: 24, color: "var(--brand-ink)" }}>{t("exchangeRates.title")}</h1>
        <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 14 }}>{t("exchangeRates.subtitle")}</p>
      </header>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <button type="button" style={secondary} onClick={() => setDay(shiftDay(day, -1))}>{t("exchangeRates.previousDay")}</button>
        <input
          type="date"
          aria-label={t("exchangeRates.day")}
          value={day}
          onChange={(e) => e.target.value && setDay(e.target.value)}
          style={dateInput}
        />
        <button type="button" style={secondary} onClick={() => setDay(shiftDay(day, 1))}>{t("exchangeRates.nextDay")}</button>
        <button type="button" style={secondary} onClick={() => setDay(todayInBelgrade())}>{t("exchangeRates.today")}</button>
      </div>

      {failed ? (
        <p style={{ color: "var(--err)", margin: 0 }}>{failed}</p>
      ) : !list ? (
        <PanelLoading text={t("common.loading")} />
      ) : !list.day ? (
        <p style={{ color: "var(--ink-3)", margin: 0 }}>{t("exchangeRates.empty")}</p>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <strong style={{ fontSize: 16, color: "var(--ink)" }}>
              {t("exchangeRates.listNumber", { number: list.listNumber ?? "—" })} · {t("exchangeRates.forDay", { day: formatDate(list.day) })}
            </strong>
            {list.documentNumber && (
              <span style={{ color: "var(--ink-3)", fontSize: 13 }}>{t("exchangeRates.erpDocument", { number: list.documentNumber })}</span>
            )}
            {list.day !== list.askedFor && (
              <span style={{ color: "var(--warn)", fontSize: 13 }}>{t("exchangeRates.earlierList", { asked: formatDate(list.askedFor) })}</span>
            )}
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={tbl}>
              <thead>
                <tr>
                  <th style={th}>{t("exchangeRates.col.currency")}</th>
                  <th style={{ ...th, textAlign: "right" }}>{t("exchangeRates.col.unit")}</th>
                  <th style={{ ...th, textAlign: "right" }}>{t("exchangeRates.col.buying")}</th>
                  <th style={{ ...th, textAlign: "right" }}>{t("exchangeRates.col.middle")}</th>
                  <th style={{ ...th, textAlign: "right" }}>{t("exchangeRates.col.selling")}</th>
                </tr>
              </thead>
              <tbody>
                {list.rates.map((r) => (
                  <tr key={r.code}>
                    <td style={{ ...td, fontWeight: 600 }}>{r.code}</td>
                    <td style={{ ...td, textAlign: "right" }}>{r.unit}</td>
                    <td style={num}>{rate(r.buying)}</td>
                    <td style={{ ...num, fontWeight: 600 }}>{rate(r.middle)}</td>
                    <td style={num}>{rate(r.selling)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const tbl: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13, background: "var(--surface)", borderRadius: 8, overflow: "hidden", boxShadow: "var(--shadow-1)" };
const th: React.CSSProperties = { textAlign: "left", padding: "10px 12px", background: "var(--surface-2)", color: "var(--ink-3)", fontWeight: 600, borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" };
const td: React.CSSProperties = { padding: "8px 12px", borderBottom: "1px solid var(--border)" };
const num: React.CSSProperties = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
const secondary: React.CSSProperties = { padding: "8px 14px", background: "var(--surface)", border: "1px solid var(--border-strong)", borderRadius: 6, fontSize: 14, cursor: "pointer", color: "var(--ink)" };
const dateInput: React.CSSProperties = { padding: "7px 10px", background: "var(--surface)", border: "1px solid var(--border-strong)", borderRadius: 6, fontSize: 14, color: "var(--ink)" };
